import { compact, difference, maxBy, minBy, range, sortBy } from 'lodash-es';
import { DateTime, Interval } from 'luxon';
import {
    AccreditationId,
    AccreditationsSlot,
    AccreditationsSlotId,
    AccreditationsSlotInput,
    DelegationAccreditationSlotFragment,
    FormAccreditationsOptionsFragment,
    FormRegisterAccreditationSlotFragment,
    PositionPositionsCustomFieldsFragment,
    RegisterAccreditationDisplay,
    RegisterPositionFilter,
    RegisterSlotDisplay,
    VolunteersRegistrationsSlotInput
} from '../generated/types';
import { DateTimeService } from '../services/dateTimeService';
import { Emptyable } from '../util/emptyable';
import { mergeOverlapping } from '../util/interval';
import { fromRange, LocaleFormats, LocaleFormatType } from '../util/luxon';
import { isNonEmptyString } from '../util/string';
import { Fields } from './field';
import { meetPositionCustomFieldsConditions } from './position';

interface IFullNameOptions {
    accreditation?: {
        includeName?: boolean;
        includeNameAtEnd?: boolean;
    };
    defaultName?: string;
    date?: {
        format?: string;
        localeFormat?: LocaleFormatType;
    };
}

export function fullName(
    dateTimeService: DateTimeService,
    slot: Pick<AccreditationsSlot, 'name' | 'date'>,
    accreditationName: string,
    options: IFullNameOptions = {}
) {
    const name = isNonEmptyString(slot.name) ? slot.name : '';
    const date = slot.date
        ? isNonEmptyString(options?.date?.format)
            ? slot.date.toFormat(options?.date?.format ?? '')
            : dateTimeService.toLocaleString(
                  slot.date,
                  options?.date?.localeFormat || LocaleFormats.DateOnly.Numeric
              )
        : '';
    const fullName = compact([
        options?.accreditation?.includeName ? accreditationName : '',
        name,
        date,
        options?.accreditation?.includeNameAtEnd ? accreditationName : ''
    ]).join(' - ');

    return isNonEmptyString(fullName)
        ? fullName
        : isNonEmptyString(options?.defaultName)
          ? options?.defaultName
          : accreditationName;
}

const accreditationSlotDateInInterval = (interval: Interval, date: DateTime) =>
    interval.contains(date) ||
    interval.start!.startOf('day').toMillis() === date.startOf('day').toMillis() ||
    interval.end!.minus({ minute: 1 }).startOf('day').toMillis() === date.startOf('day').toMillis();

export function canSelect(
    slot: FormRegisterAccreditationSlotFragment,
    ranges: Array<Interval | VolunteersRegistrationsSlotInput>,
    options: FormAccreditationsOptionsFragment,
    delegationAccreditationsSlots: DelegationAccreditationSlotFragment[]
): boolean {
    // we remove 1 minute from interval end, because availabilities are stored
    // from midnight to midnight of the day after, eg:
    // 2024-12-23 00:00 - 2024-12-24 00:00 for the 23rd of December
    // in this case we want to show accreditations for the 23rd only, not the 24th

    return (
        (!slot.isBlocked &&
            options.accreditationDisplay !== RegisterAccreditationDisplay.None &&
            !options.hiddenAccreditationsSlotsIds.includes(slot.id) &&
            (options.displayedAccreditationsSlotsIds.length === 0 ||
                options.displayedAccreditationsSlotsIds.includes(slot.id)) &&
            (options.showFullAccreditation || !slot.isFull) &&
            (options.slotDisplay === RegisterSlotDisplay.Hide ||
                options.accreditationFilter === RegisterPositionFilter.None ||
                !slot.date ||
                mergeOverlapping(ranges.map(fromRange)).some((interval) =>
                    accreditationSlotDateInInterval(interval, slot.date!)
                ))) ||
        delegationAccreditationsSlots.some(
            (da) =>
                da.accreditationSlotId === slot.id &&
                !da.accreditationSlot.isBlocked &&
                (options.slotDisplay === RegisterSlotDisplay.Hide ||
                    options.accreditationFilter === RegisterPositionFilter.None ||
                    !da.accreditationSlot?.date ||
                    mergeOverlapping(ranges.map(fromRange)).some((interval) =>
                        accreditationSlotDateInInterval(interval, da.accreditationSlot.date!)
                    ))
        )
    );
}

export function repeatSlot(
    slot: AccreditationsSlotInput,
    value: number,
    unit: string
): AccreditationsSlotInput[] {
    if (slot.date?.isValid) {
        return range(1, value + 1).map((i) => {
            if (unit === 'day') {
                const newDate = slot.date!.plus({ day: i });

                return {
                    name: slot.name,
                    date: newDate,
                    maxResources: slot.maxResources
                };
            } else if (unit === 'week') {
                const newDate = slot.date!.plus({ week: i });

                return {
                    name: slot.name,
                    date: newDate,
                    maxResources: slot.maxResources
                };
            } else {
                throw new Error('Wrong unit');
            }
        });
    } else {
        throw new Error('Can not repeat slot with wrong date');
    }
}

interface IDateSlotsStatsReturn {
    minDate: DateTime;
    maxDate: DateTime;
    numberOfMonths: number;
}

export function dateSlotsStats(
    slots: Array<Pick<AccreditationsSlot, 'date'>>
): IDateSlotsStatsReturn {
    const slotsWithDates = slots.filter((s) => s.date?.isValid);
    const minDate = minBy(
        slotsWithDates.map((s) => s.date!),
        (d) => d.toMillis()
    )!;
    const maxDate = maxBy(
        slotsWithDates.map((s) => s.date!),
        (d) => d.toMillis()
    )!;
    const numberOfMonths = Math.ceil(
        maxDate.endOf('month').diff(minDate.startOf('month'), 'months').months
    );

    return {
        minDate,
        maxDate,
        numberOfMonths
    };
}

export function newCalendarsAccreditationsSlotsIds(
    currentAccreditationsSlotsIds: AccreditationsSlotId[],
    accreditationsSlots: Array<Pick<AccreditationsSlot, 'id' | 'date'>>,
    firstDayOfMonth: DateTime,
    newDateTimes: DateTime[]
) {
    const accreditationsSlodsIdsToKeep = difference(
        currentAccreditationsSlotsIds,
        accreditationsSlots.flatMap((slot) =>
            slot.date?.isValid && firstDayOfMonth.equals(slot.date!.startOf('month'))
                ? [slot.id]
                : []
        )
    );
    const newAccreditationsSlotsIds = accreditationsSlots.flatMap((slot) => {
        const isSelected = newDateTimes.some(
            (date) => slot.date?.isValid && date.startOf('day').equals(slot.date!.startOf('day'))
        );

        return isSelected ? [slot.id] : [];
    });

    return newAccreditationsSlotsIds.concat(accreditationsSlodsIdsToKeep);
}

export function filterByConditionsAndAvailabilities(
    accreditationsSlots: AccreditationsSlot[],
    accreditationsCustomFieldsConditions: Record<
        AccreditationId,
        PositionPositionsCustomFieldsFragment
    >,
    userFields: Fields,
    slotDisplay: RegisterSlotDisplay,
    addDefaultAccreditationsByAvailability: boolean,
    slots: VolunteersRegistrationsSlotInput[]
): AccreditationsSlotId[] {
    if (slotDisplay === RegisterSlotDisplay.Hide || !addDefaultAccreditationsByAvailability) {
        return accreditationsSlots
            .filter((as) => {
                const customFieldsConditions =
                    accreditationsCustomFieldsConditions[as.accreditationId];

                return meetPositionCustomFieldsConditions(customFieldsConditions, userFields);
            })
            .map((as) => as.id);
    } else {
        const intervals = mergeOverlapping(slots.map(fromRange));

        return accreditationsSlots
            .filter((as) => {
                const customFieldsConditions =
                    accreditationsCustomFieldsConditions[as.accreditationId];

                return meetPositionCustomFieldsConditions(customFieldsConditions, userFields);
            })
            .filter((as) => {
                if (as.date) {
                    return intervals.some((i) => accreditationSlotDateInInterval(i, as.date!));
                } else {
                    return true;
                }
            })
            .map((as) => as.id);
    }
}

export function sortedSlots<T extends { date?: Emptyable<DateTime>; name: string }>(
    slots: T[]
): T[] {
    return sortBy(slots, (s) => [s.date?.toMillis() ?? 1, s.name.toLowerCase()]);
}

export function isPast(slot: Pick<AccreditationsSlot, 'date'>): boolean {
    return slot.date?.isValid ? slot.date.endOf('day') < DateTime.utc().startOf('day') : false;
}
